import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createModel } from '../api/models';
import { uploadFile } from '../api/files';
import { suggestTags } from '../api/tags';
import { getModelByUsername } from '../api/models';
import { AdminCreateModelDto } from '../api/types';

interface CreateModelFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const CreateModelForm: React.FC<CreateModelFormProps> = ({ isOpen, onClose, onSuccess }) => {
    const queryClient = useQueryClient();

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<AdminCreateModelDto>({
        modelUsername: '',
        modelName: '',
        description: '',
        price: '',
        onlyfansUrl: '',
        imageFilename: '',
        promote: false,
        blacklisted: false,
        tags: [],
        likesCount: 0
    });

    const [tagInput, setTagInput] = useState('');
    const [tagSuggestions, setTagSuggestions] = useState<any[]>([]);
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [dragActive, setDragActive] = useState(false);
    const [usernameCheck, setUsernameCheck] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

    const steps = [
        { id: 1, title: 'Basic Info', icon: '👤' },
        { id: 2, title: 'Image & Details', icon: '📸' },
        { id: 3, title: 'Tags & Settings', icon: '🏷️' },
        { id: 4, title: 'Review', icon: '✅' }
    ];

    // Validation functions
    const validateStep1 = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.modelUsername?.trim()) {
            newErrors.modelUsername = 'Username is required';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.modelUsername)) {
            newErrors.modelUsername = 'Only letters, numbers, hyphens and underscores allowed';
        }

        if (formData.onlyfansUrl && !isValidUrl(formData.onlyfansUrl)) {
            newErrors.onlyfansUrl = 'Please enter a valid URL';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (formData.price && !/^\$?\d+(\.\d{2})?$/.test(formData.price)) {
            newErrors.price = 'Price format: $XX.XX or XX.XX';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Mutations
    const createMutation = useMutation({
        mutationFn: createModel,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['models'] });
            onSuccess?.();
            handleClose();
        },
        onError: (error: any) => {
            setErrors({ submit: 'Failed to create model. Please try again.' });
        }
    });

    const uploadMutation = useMutation({
        mutationFn: ({ file, desiredName }: { file: File; desiredName?: string }) =>
            uploadFile(file, desiredName),
        onSuccess: (data) => {
            const key = Object.values(data)[0] as string;
            setFormData(prev => ({ ...prev, imageFilename: key }));
            setUploadingImage(false);
        },
        onError: () => {
            setUploadingImage(false);
            setErrors(prev => ({ ...prev, image: 'Failed to upload image' }));
        }
    });

    // Event handlers
    const handleInputChange = (field: keyof AdminCreateModelDto, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setErrors(prev => ({ ...prev, image: 'Please select an image file' }));
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setErrors(prev => ({ ...prev, image: 'Image size should be less than 5MB' }));
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);

        setUploadingImage(true);
        const desiredName = `${formData.modelUsername || 'model'}-avatar-${Date.now()}`;
        uploadMutation.mutate({ file, desiredName });
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            const mockEvent = { target: { files: [file] } } as any;
            handleImageUpload(mockEvent);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleTagInput = async (value: string) => {
        setTagInput(value);

        if (value.length >= 2) {
            try {
                const suggestions = await suggestTags(value);
                setTagSuggestions(suggestions);
                setShowTagSuggestions(true);
            } catch {
                setTagSuggestions([]);
                setShowTagSuggestions(false);
            }
        } else {
            setShowTagSuggestions(false);
        }
    };

    const addTag = (tag: string) => {
        if (tag && !formData.tags?.includes(tag)) {
            setFormData(prev => ({
                ...prev,
                tags: [...(prev.tags || []), tag]
            }));
        }
        setTagInput('');
        setShowTagSuggestions(false);
    };

    const removeTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
        }));
    };

    const verifyUsernameUnique = async (): Promise<boolean> => {
        const username = formData.modelUsername?.trim();
        if (!username) {
            setErrors(prev => ({ ...prev, modelUsername: 'Username is required' }));
            return false;
        }
        setUsernameCheck('checking');
        try {
            await getModelByUsername(username);
            setUsernameCheck('taken');
            setErrors(prev => ({ ...prev, modelUsername: 'Username already exists' }));
            return false;
        } catch {
            setUsernameCheck('available');
            setErrors(prev => ({ ...prev, modelUsername: '' }));
            return true;
        }
    };

    const nextStep = async () => {
        if (currentStep === 1) {
            if (!validateStep1()) return;
            // Enforce uniqueness on step 1
            const ok = await verifyUsernameUnique();
            if (!ok) return;
        }
        if (currentStep === 2 && !validateStep2()) return;

        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = () => {
        createMutation.mutate({
            ...formData,
            likesCount: formData.likesCount || 0
        });
    };

    const handleClose = () => {
        setCurrentStep(1);
        setFormData({
            modelUsername: '',
            modelName: '',
            description: '',
            price: '',
            onlyfansUrl: '',
            imageFilename: '',
            promote: false,
            blacklisted: false,
            tags: [],
            likesCount: 0
        });
        setTagInput('');
        setImagePreview(null);
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal modal-xl">
                <div className="modal-header">
                    <div>
                        <h2 className="modal-title">Create New Model</h2>
                        <p className="text-sm text-gray-500 mt-1">Step {currentStep} of {steps.length}</p>
                    </div>
                    <button onClick={handleClose} className="btn btn-ghost btn-icon-only">✕</button>
                </div>
                {/* Progress Steps */}
                <div style={{ padding: '0 2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                        {steps.map((step, index) => (
                            <div key={step.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flex: 1,
                                position: 'relative'
                            }}>
                                {index < steps.length - 1 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '1.25rem',
                                        left: '50%',
                                        width: '100%',
                                        height: '2px',
                                        background: currentStep > step.id ? 'var(--color-primary)' : 'var(--color-gray-200)',
                                        zIndex: 0
                                    }} />
                                )}
                                <div style={{
                                    width: '2.5rem',
                                    height: '2.5rem',
                                    borderRadius: '50%',
                                    background: currentStep >= step.id ? 'var(--color-primary)' : 'var(--color-gray-200)',
                                    color: currentStep >= step.id ? 'white' : 'var(--color-gray-500)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.25rem',
                                    fontWeight: '600',
                                    zIndex: 1,
                                    transition: 'all 0.3s ease'
                                }}>
                                    {currentStep > step.id ? '✓' : step.icon}
                                </div>
                                <span style={{
                                    marginTop: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: currentStep === step.id ? '600' : '500',
                                    color: currentStep >= step.id ? 'var(--color-primary)' : 'var(--color-gray-500)'
                                }}>
                  {step.title}
                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-body">
                    {errors.submit && (
                        <div className="notification-error" style={{ marginBottom: '1.5rem' }}>
                            {errors.submit}
                        </div>
                    )}

                    {/* Step 1: Basic Info */}
                    {currentStep === 1 && (
                        <div style={{ display: 'grid', gap: '1.5rem' }}>
                            <div className="form-group">
                                <label className="form-label form-label-required">Model Username</label>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <input
                                        type="text"
                                        className={`form-input ${errors.modelUsername ? 'error' : ''}`}
                                        value={formData.modelUsername}
                                        onChange={(e) => {
                                            handleInputChange('modelUsername', e.target.value);
                                            setUsernameCheck('idle');
                                        }}
                                        placeholder="e.g., sarah_fitness_model"
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={async () => {
                                            const u = formData.modelUsername?.trim();
                                            if (!u) return;
                                            await verifyUsernameUnique();
                                        }}
                                    >
                                        {usernameCheck === 'checking' ? 'Checking...' : 'Verify'}
                                    </button>
                                </div>
                                {usernameCheck === 'available' && !errors.modelUsername && (
                                    <div className="form-help" style={{ color: 'var(--color-success)' }}>Username is available</div>
                                )}
                                {errors.modelUsername && (
                                    <div className="form-error">⚠️ {errors.modelUsername}</div>
                                )}
                                <div className="form-help">
                                    This will be the unique identifier for the model
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Display Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.modelName || ''}
                                    onChange={(e) => handleInputChange('modelName', e.target.value)}
                                    placeholder="e.g., Sarah K"
                                />
                                <div className="form-help">
                                    The name that will be displayed to users
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">OnlyFans URL</label>
                                <input
                                    type="url"
                                    className={`form-input ${errors.onlyfansUrl ? 'error' : ''}`}
                                    value={formData.onlyfansUrl || ''}
                                    onChange={(e) => handleInputChange('onlyfansUrl', e.target.value)}
                                    placeholder="https://onlyfans.com/username"
                                />
                                {errors.onlyfansUrl && (
                                    <div className="form-error">⚠️ {errors.onlyfansUrl}</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Description</label>
                                <textarea
                                    className="form-input form-textarea"
                                    value={formData.description || ''}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder="Brief description of the model..."
                                    rows={4}
                                />
                                <div className="form-help">
                                    A brief description that will help users understand what this model offers
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Image & Details */}
                    {currentStep === 2 && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                            <div>
                                <div className="form-group">
                                    <label className="form-label">Profile Image</label>
                                    <div
                                        className={`form-input`}
                                        style={{
                                            height: '200px',
                                            border: `2px dashed ${dragActive ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
                                            background: dragActive ? 'var(--color-primary-light)' : imagePreview ? 'transparent' : 'var(--color-gray-50)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                        onClick={() => !uploadingImage && document.getElementById('image-upload')?.click()}
                                    >
                                        {imagePreview ? (
                                            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                {uploadingImage && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background: 'rgba(0,0,0,0.5)',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        borderRadius: '8px'
                                                    }}>
                                                        <div className="loading-spinner" />
                                                        <p style={{ marginTop: '1rem' }}>Uploading...</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                                                    {uploadingImage ? '⏳' : dragActive ? '📤' : '📷'}
                                                </div>
                                                <p style={{ color: 'var(--color-gray-600)', marginBottom: '0.5rem' }}>
                                                    {uploadingImage ? 'Uploading...' : dragActive ? 'Drop image here' : 'Click or drag image here'}
                                                </p>
                                                <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                                                    PNG, JPG, GIF up to 5MB
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <input
                                        type="file"
                                        id="image-upload"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                        disabled={uploadingImage}
                                    />

                                    {errors.image && (
                                        <div className="form-error">⚠️ {errors.image}</div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="form-group">
                                    <label className="form-label">Price</label>
                                    <input
                                        type="text"
                                        className={`form-input ${errors.price ? 'error' : ''}`}
                                        value={formData.price || ''}
                                        onChange={(e) => handleInputChange('price', e.target.value)}
                                        placeholder="e.g., $9.99"
                                    />
                                    {errors.price && (
                                        <div className="form-error">⚠️ {errors.price}</div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Likes Count</label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        value={formData.likesCount || ''}
                                        onChange={(e) => handleInputChange('likesCount', parseInt(e.target.value) || 0)}
                                        min="0"
                                        placeholder="0"
                                    />
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Step 3: Tags & Settings */}
                    {currentStep === 3 && (
                        <div style={{ display: 'grid', gap: '2rem' }}>
                            <div className="form-group">
                                <label className="form-label">Tags</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={tagInput}
                                        onChange={(e) => handleTagInput(e.target.value)}
                                        placeholder="Type to search or add tags..."
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                if (tagInput.trim()) {
                                                    addTag(tagInput.trim());
                                                }
                                            }
                                        }}
                                    />

                                    {showTagSuggestions && tagSuggestions.length > 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            background: 'white',
                                            border: '1px solid var(--color-gray-200)',
                                            borderRadius: 'var(--radius-md)',
                                            boxShadow: 'var(--shadow-lg)',
                                            zIndex: 10,
                                            maxHeight: '200px',
                                            overflowY: 'auto'
                                        }}>
                                            {tagSuggestions.map((suggestion, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    onClick={() => addTag(suggestion.slug)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem 1rem',
                                                        textAlign: 'left',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-gray-50)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <div style={{ fontWeight: '500' }}>{suggestion.name}</div>
                                                    <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                                                        {suggestion.slug} • {suggestion.modelCount} models
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {formData.tags && formData.tags.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
                                        {formData.tags.map((tag) => (
                                            <span key={tag} className="badge badge-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => removeTag(tag)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'inherit',
                                                        cursor: 'pointer',
                                                        padding: 0,
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                          ✕
                        </button>
                      </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Status Settings</h3>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                className="form-checkbox"
                                                checked={formData.promote || false}
                                                onChange={(e) => handleInputChange('promote', e.target.checked)}
                                            />
                                            <div>
                                                <div style={{ fontWeight: '500' }}>🚀 Promote Model</div>
                                                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                                                    Featured in promoted sections
                                                </div>
                                            </div>
                                        </label>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                className="form-checkbox"
                                                checked={formData.blacklisted || false}
                                                onChange={(e) => handleInputChange('blacklisted', e.target.checked)}
                                            />
                                            <div>
                                                <div style={{ fontWeight: '500' }}>🚫 Blacklisted</div>
                                                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                                                    Hidden from public listings
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Preview</h3>

                                    <div className="card" style={{ padding: '1rem', background: 'var(--color-gray-50)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                            <div className="model-avatar" style={{ width: '2.5rem', height: '2.5rem', fontSize: '1rem' }}>
                                                {(formData.modelName || formData.modelUsername || 'M').charAt(0)}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '600' }}>
                                                    {formData.modelName || formData.modelUsername || 'Model Name'}
                                                </div>
                                                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                                                    @{formData.modelUsername || 'username'}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', display: 'grid', gap: '0.25rem' }}>
                                            <div>📷 Image: {formData.imageFilename ? '✅' : '❌'}</div>
                                            <div>🏷️ Tags: {formData.tags?.length || 0}</div>
                                            <div>💰 Price: {formData.price || 'Not set'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Review */}
                    {currentStep === 4 && (
                        <div style={{ display: 'grid', gap: '2rem' }}>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem' }}>Review & Create Model</h3>
                                <p style={{ color: 'var(--color-gray-600)' }}>
                                    Please review all information before creating the model. You can edit these details later.
                                </p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                <div className="card">
                                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Basic Information</h4>
                                    <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.875rem' }}>
                                        <div><strong>Username:</strong> {formData.modelUsername}</div>
                                        <div><strong>Display Name:</strong> {formData.modelName || 'Not set'}</div>
                                        <div><strong>Description:</strong> {formData.description || 'Not set'}</div>
                                        <div><strong>OnlyFans URL:</strong> {formData.onlyfansUrl || 'Not set'}</div>
                                        <div><strong>Price:</strong> {formData.price || 'Not set'}</div>
                                    </div>
                                </div>

                                <div className="card">
                                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Settings & Media</h4>
                                    <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.875rem' }}>
                                        <div><strong>Profile Image:</strong> {formData.imageFilename ? '✅ Uploaded' : '❌ No image'}</div>
                                        <div><strong>Likes Count:</strong> {formData.likesCount || 0}</div>
                                        <div><strong>Status:</strong>
                                            {formData.promote && <span className="badge badge-success" style={{ marginLeft: '0.5rem' }}>Promoted</span>}
                                            {formData.blacklisted && <span className="badge badge-danger" style={{ marginLeft: '0.5rem' }}>Blacklisted</span>}
                                            {!formData.promote && !formData.blacklisted && <span style={{ marginLeft: '0.5rem', color: 'var(--color-gray-500)' }}>Normal</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="card" style={{ gridColumn: 'span 2' }}>
                                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                                        Tags ({formData.tags?.length || 0})
                                    </h4>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {formData.tags && formData.tags.length > 0 ? (
                                            formData.tags.map((tag) => (
                                                <span key={tag} className="badge badge-primary">{tag}</span>
                                            ))
                                        ) : (
                                            <span style={{ color: 'var(--color-gray-500)', fontStyle: 'italic' }}>No tags selected</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button
                        onClick={handleClose}
                        className="btn btn-secondary"
                        disabled={createMutation.isPending}
                    >
                        Cancel
                    </button>

                    {currentStep > 1 && (
                        <button
                            onClick={prevStep}
                            className="btn btn-ghost"
                            disabled={createMutation.isPending}
                        >
                            ← Previous
                        </button>
                    )}

                    {currentStep < 4 ? (
                        <button
                            onClick={nextStep}
                            className="btn btn-primary"
                            disabled={currentStep === 1 && (usernameCheck === 'taken' || !!errors.modelUsername)}
                        >
                            Next →
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            className="btn btn-success"
                            disabled={createMutation.isPending || uploadingImage}
                        >
                            {createMutation.isPending ? (
                                <>
                                    <div className="loading-spinner" style={{ width: '1rem', height: '1rem' }} />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    ✅ Create Model
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateModelForm;